import React from "react";
import Box from "@mui/material/Box";
import Slider from "@mui/material/Slider";
import { styled } from "@mui/material/styles";
import { AppState } from "./Types";
import ToggleButton from "@mui/material/ToggleButton";
import PsychologyIcon from "@mui/icons-material/Psychology";
import RepeatIcon from "@mui/icons-material/Repeat";
import TimelineIcon from "@mui/icons-material/Timeline";
import { speedMarks } from "./Components";
import { valueText } from "./Functions";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import { Divider, Grow, Stack, Typography } from "@mui/material";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import BoardControls from "./BoardControls";
import Icon from "@mdi/react";
import { mdiTortoise, mdiRabbit } from "@mdi/js";

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }}>
    <span>{props.children}</span>
  </Tooltip>
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.primary.main,
    color: "rgba(0, 0, 0, 0.87)",
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(13),
  },
}));

function KnightControls({
  appState,
  setAppState,
}: {
  appState: AppState;
  setAppState: React.Dispatch<React.SetStateAction<AppState>>;
}) {
  const changeSpeed = (event: Event, newSpeed: number | number[]) => {
    setAppState({
      ...appState,
      knightSpeed: 1000 - (newSpeed as number),
    });
  };

  let knightMoveWords = "";

  if (appState.moving) {
    knightMoveWords += "moving";
    knightMoveWords += appState.heuristic ? " automatically" : " randomly";
  } else {
    knightMoveWords += "controlled by you";
  }

  return (
    <Grow
      in={true}
      style={{ transformOrigin: "0 0 0" }}
      {...(true ? { timeout: 800 } : {})}
    >
      <div id="controls" className="box surface">
        <div className="box-title">
          <SettingsOutlinedIcon fontSize="small" sx={{ marginRight: "4px" }} />
          Controls
        </div>
        <Box sx={{ width: "86%", paddingBottom: "1rem" }}>
          <BoardControls appState={appState} setAppState={setAppState} />
          <Divider sx={{ marginY: "0.65rem" }} />
          <div className="col">
            <Typography
              // mt={1}
              color={"#b8b8b8"}
              variant="body2"
              fontSize={"0.8rem"}
              align="center"
            >
              The board is{" "}
              <Typography
                component={"span"}
                color={"primary"}
                variant="body2"
                fontSize={"0.8rem"}
              >
                {" "}
                {appState.boardSize}x{appState.boardSize}{" "}
              </Typography>{" "}
              squares large.
            </Typography>
            <Typography
              mt={0.4}
              color={"#b8b8b8"}
              variant="body2"
              fontSize={"0.8rem"}
              align="center"
            >
              The knight is{" "}
              <Typography
                component={"span"}
                color={"primary"}
                variant="body2"
                fontSize={"0.8rem"}
              >
                {knightMoveWords}
              </Typography>
              .
            </Typography>
            <Typography
              mt={0.4}
              mb={0.15}
              color={"#b8b8b8"}
              variant="body2"
              fontSize={"0.8rem"}
              align="center"
            >
              The knight is{" "}
              <Typography
                component={"span"}
                color={"primary"}
                variant="body2"
                fontSize={"0.8rem"}
              >
                {appState.looping ? "determined" : "ready to give up"}
              </Typography>
              .
            </Typography>{" "}
          </div>
          <Divider sx={{ marginY: "0.65rem" }} />
          <Stack
            spacing={2}
            direction="row"
            mt={1.4}
            mb={1}
            alignItems="center"
            justifyContent="center"
          >
            <StyledTooltip
              placement="top"
              title={(appState.moving ? "Stop" : "Auto-move") + " knight"}
            >
              <ToggleButton
                value="check"
                selected={appState.moving}
                color="primary"
                onChange={() => {
                  let newLoop =
                    appState.looping && appState.moving
                      ? false
                      : appState.looping;
                  let newHeu =
                    appState.heuristic && appState.moving
                      ? false
                      : appState.heuristic;
                  setAppState({
                    ...appState,
                    moving: !appState.moving,
                    looping: newLoop,
                    heuristic: newHeu,
                  });
                }}
              >
                <TimelineIcon fontSize="medium" />
              </ToggleButton>
            </StyledTooltip>

            <StyledTooltip
              placement="top"
              title={(appState.moving ? "Use" : "Stop using") + " heuristic"}
            >
              <ToggleButton
                disabled={!appState.moving}
                value="check"
                color="primary"
                selected={appState.heuristic}
                onChange={() => {
                  setAppState({
                    ...appState,
                    heuristic: !appState.heuristic,
                  });
                }}
              >
                <PsychologyIcon fontSize="medium" />
              </ToggleButton>
            </StyledTooltip>

            <StyledTooltip
              placement="top"
              title={(appState.looping ? "Stop" : "Start") + " looping"}
            >
              <ToggleButton
                disabled={!appState.moving}
                value="check"
                color="primary"
                selected={appState.looping}
                onChange={() => {
                  setAppState({
                    ...appState,
                    looping: !appState.looping,
                  });
                }}
              >
                <RepeatIcon fontSize="medium" />
              </ToggleButton>
            </StyledTooltip>
          </Stack>
          {appState.moving && (
            <Box>
              <Grow
                in={appState.moving}
                {...(appState.moving ? { timeout: 1000 } : {})}
              >
                <Stack spacing={2} direction="row" mt={1.1} alignItems="center">
                  <Icon path={mdiTortoise} size={1.425} />
                  <Slider
                    aria-label="Speed marks"
                    defaultValue={500}
                    value={1000 - appState.knightSpeed}
                    getAriaValueText={valueText}
                    step={50}
                    valueLabelDisplay="auto"
                    min={50}
                    max={950}
                    shiftStep={50}
                    onChange={changeSpeed}
                    valueLabelFormat={valueText}
                  />
                  <Icon path={mdiRabbit} size={1.425} />
                </Stack>
              </Grow>
            </Box>
          )}
        </Box>
      </div>
    </Grow>
  );
}

export default KnightControls;
